import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { FilterBadge } from "@package/subframe/components/FilterBadge";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Select } from "@package/subframe/components/Select";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMoreHorizontal,
  FeatherPlus,
  FeatherShieldCheck,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { addDays, format, isBefore, isPast } from "date-fns";
import { useEffect, useState } from "react";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import {
  TABLE_MIN_WIDTH,
  TableScrollContainer,
} from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useListTaskDefinitionsByNurseryQueryControllerList } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskDefinitionResponseDto } from "@/generated/model/complianceTaskDefinitionResponseDto";
import { PAGE_SIZE } from "@/lib/compliance-manager";
import { totalPages } from "@/lib/pagination";

import {
  getStatusKey,
  ROLE_DISPLAY,
  sortCategoryIdsByCatalog,
  STATUS_BADGE_VARIANT,
  STATUS_DISPLAY,
} from "./compliance-constants";
import { DeactivateDefinitionDialog } from "./DeactivateDefinitionDialog";
import { DeleteDefinitionDialog } from "./DeleteDefinitionDialog";

interface ComplianceTaskListProps {
  /**
   * Rendered inside Compliance Manager's Tasks tab, which supplies the page
   * header and the Add task button.
   */
  embedded?: boolean;
}

export function ComplianceTaskList({
  embedded = false,
}: ComplianceTaskListProps) {
  const navigate = useNavigate();
  const {
    selectedNurseryId,
    selectedNursery,
    isLoading: nurseryLoading,
  } = useNursery();
  const { userRoleType, isPlatformAdmin } = useAuth();

  // Only group (organisation) admins manage compliance tasks. Nursery members,
  // managers included, get a read-only view: the create button and per-row
  // actions are hidden, and the API enforces the same restriction.
  const canManage = isPlatformAdmin || userRoleType === "ORG_ADMIN";

  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    undefined,
  );
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Category catalog → id→name map for rendering, and options for the filter.
  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] =
    useState<ComplianceTaskDefinitionResponseDto | null>(null);

  const { data: listData, isLoading: listLoading } =
    useListTaskDefinitionsByNurseryQueryControllerList(
      selectedNurseryId ?? "",
      {
        categoryId: categoryFilter,
        isActive: showInactive ? undefined : true,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      { query: { enabled: !!selectedNurseryId } },
    );

  const definitions: ComplianceTaskDefinitionResponseDto[] =
    (listData?.data as any)?.data ?? [];
  const total: number = (listData?.data as any)?.meta?.total ?? 0;

  // Deactivating or deleting the last row on the final page shrinks the total,
  // which would otherwise leave `page` past the end and the table empty. The
  // total only arrives with the response, so the correction happens after it.
  useEffect(() => {
    const pageCount = totalPages(total, pageSize);
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [total, pageSize, page]);

  if (nurseryLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (!selectedNurseryId) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 py-24">
        <IconWithLogoBackground
          variant="neutral"
          size="x-large"
          icon={<FeatherShieldCheck />}
        />
        <span className="text-heading-3 font-heading-3 text-default-font">
          No nursery selected
        </span>
        <span className="text-body font-body text-subtext-color">
          Select a nursery from the sidebar to manage compliance tasks.
        </span>
      </div>
    );
  }

  const handleOpenDeactivateDialog = (
    def: ComplianceTaskDefinitionResponseDto,
  ) => {
    setSelectedDefinition(def);
    setDeactivateDialogOpen(true);
  };

  const handleOpenDeleteDialog = (def: ComplianceTaskDefinitionResponseDto) => {
    setSelectedDefinition(def);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Header */}
      {!embedded && (
        <div className="flex w-full flex-col items-start gap-1">
          <div className="flex w-full flex-wrap items-center gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                Compliance Tasks
              </span>
              <span className="text-body font-body text-subtext-color">
                Manage compliance task definitions for {selectedNursery?.name}
              </span>
            </div>
            {canManage && (
              <Button
                icon={<FeatherPlus />}
                onClick={() => navigate({ to: "/compliance/new" })}
              >
                Add task
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex w-full items-center gap-2">
        <Select
          placeholder="All categories"
          value={categoryFilter ?? "ALL"}
          onValueChange={(val) => {
            setCategoryFilter(val === "ALL" ? undefined : val);
            setPage(1);
          }}
        >
          <Select.Item value="ALL">All categories</Select.Item>
          {categories.map((category) => (
            <Select.Item key={category.id} value={category.id}>
              {category.name}
            </Select.Item>
          ))}
        </Select>
        <FilterBadge
          label="Show inactive"
          selected={showInactive}
          onClick={() => {
            setShowInactive(!showInactive);
            setPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="flex w-full flex-col items-start gap-4 pb-8">
        {listLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : definitions.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-4 py-24">
            <IconWithLogoBackground
              variant="neutral"
              size="x-large"
              icon={<FeatherShieldCheck />}
            />
            <span className="text-heading-3 font-heading-3 text-default-font">
              No compliance tasks yet
            </span>
            <span className="text-body font-body text-subtext-color">
              {canManage
                ? "Create your first compliance task to get started."
                : "There are no compliance tasks for this nursery yet."}
            </span>
            {canManage && !embedded && (
              <Button
                icon={<FeatherPlus />}
                onClick={() => navigate({ to: "/compliance/new" })}
              >
                Add task
              </Button>
            )}
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <Table
                className={TABLE_MIN_WIDTH.md}
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Task</Table.HeaderCell>
                    <Table.HeaderCell>Categories</Table.HeaderCell>
                    <Table.HeaderCell>Assigned to</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Next due</Table.HeaderCell>
                    {canManage && (
                      <Table.HeaderCell variant="aligned-right">
                        Actions
                      </Table.HeaderCell>
                    )}
                  </Table.HeaderRow>
                }
              >
                {definitions.map((def) => {
                  const statusKey = getStatusKey(def.latestInstanceStatus);
                  const nextDue = def.nextDueDate
                    ? new Date(def.nextDueDate)
                    : null;
                  const isOverdue = nextDue
                    ? isPast(nextDue) && statusKey === "OVERDUE"
                    : false;
                  const isDueSoon = nextDue
                    ? isBefore(nextDue, addDays(new Date(), 7)) &&
                      statusKey === "DUE_SOON"
                    : false;

                  return (
                    <Table.Row
                      key={def.id}
                      clickable={canManage}
                      onClick={
                        canManage
                          ? () => navigate({ to: `/compliance/${def.id}/edit` })
                          : undefined
                      }
                    >
                      <Table.Cell className="h-14 grow shrink-0 basis-0">
                        <div className="flex flex-col items-start">
                          <span className="text-body-bold font-body-bold text-neutral-900">
                            {def.name}
                          </span>
                          {def.description && (
                            <span className="text-caption font-caption text-subtext-color line-clamp-1">
                              {def.description}
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="h-14 grow shrink-0 basis-0">
                        <div className="flex flex-wrap items-center gap-1">
                          {def.primaryCategoryId && (
                            <Badge variant="brand">
                              {categoryNameById.get(def.primaryCategoryId) ??
                                "Unknown"}
                            </Badge>
                          )}
                          {sortCategoryIdsByCatalog(
                            def.categoryIds.filter(
                              (id) => id !== def.primaryCategoryId,
                            ),
                            categories,
                          ).map((id) => (
                            <Badge key={id} variant="neutral">
                              {categoryNameById.get(id) ?? "Unknown"}
                            </Badge>
                          ))}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="h-14 grow shrink-0 basis-0">
                        <span className="text-caption font-caption text-neutral-900">
                          {def.assigneeRole
                            ? (ROLE_DISPLAY[def.assigneeRole] ??
                              def.assigneeRole)
                            : def.assigneeUserId
                              ? "Specific person"
                              : "Not assigned"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="h-14 grow shrink-0 basis-0">
                        <Badge variant={STATUS_BADGE_VARIANT[statusKey]}>
                          {STATUS_DISPLAY[statusKey]}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="h-14 grow shrink-0 basis-0">
                        <span
                          className={`text-caption font-caption ${
                            isOverdue
                              ? "text-error-600"
                              : isDueSoon
                                ? "text-warning-700"
                                : "text-neutral-900"
                          }`}
                        >
                          {nextDue ? format(nextDue, "d MMM yyyy") : "—"}
                        </span>
                      </Table.Cell>
                      {canManage && (
                        <Table.Cell
                          className="h-14 grow shrink-0 basis-0"
                          variant="aligned-right"
                        >
                          <div
                            className="flex items-center justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SubframeCore.DropdownMenu.Root>
                              <SubframeCore.DropdownMenu.Trigger asChild>
                                <IconButton
                                  variant="neutral-tertiary"
                                  icon={<FeatherMoreHorizontal />}
                                  data-testid="compliance-row-actions"
                                />
                              </SubframeCore.DropdownMenu.Trigger>
                              <SubframeCore.DropdownMenu.Portal>
                                <SubframeCore.DropdownMenu.Content
                                  side="bottom"
                                  align="end"
                                  sideOffset={4}
                                  asChild
                                >
                                  <DropdownMenu>
                                    <DropdownMenu.DropdownItem
                                      icon={null}
                                      onSelect={() =>
                                        navigate({
                                          to: `/compliance/${def.id}/edit`,
                                        })
                                      }
                                    >
                                      Edit
                                    </DropdownMenu.DropdownItem>
                                    {def.isActive && (
                                      <DropdownMenu.DropdownItem
                                        icon={null}
                                        onSelect={() =>
                                          handleOpenDeactivateDialog(def)
                                        }
                                      >
                                        Deactivate
                                      </DropdownMenu.DropdownItem>
                                    )}
                                    <DropdownMenu.DropdownItem
                                      icon={null}
                                      onSelect={() =>
                                        handleOpenDeleteDialog(def)
                                      }
                                    >
                                      Delete
                                    </DropdownMenu.DropdownItem>
                                  </DropdownMenu>
                                </SubframeCore.DropdownMenu.Content>
                              </SubframeCore.DropdownMenu.Portal>
                            </SubframeCore.DropdownMenu.Root>
                          </div>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  );
                })}
              </Table>
            </TableScrollContainer>

            <Pagination
              page={page}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemNoun="tasks"
            />
          </>
        )}
      </div>

      {/* Deactivate (archive) Dialog */}
      {selectedDefinition && (
        <DeactivateDefinitionDialog
          open={deactivateDialogOpen}
          onOpenChange={setDeactivateDialogOpen}
          definition={selectedDefinition}
        />
      )}

      {/* Delete Definition Dialog */}
      {selectedDefinition && (
        <DeleteDefinitionDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          definition={selectedDefinition}
        />
      )}
    </div>
  );
}
